import React, { useState, useEffect } from 'react';
import axios from '../api';
import { Box, TextField, Button, Alert, MenuItem, Typography, CircularProgress } from '@mui/material';
import { useUser } from './UserContext';

const labelSx = { fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'text.secondary', mb: 0.5, display: 'block' };
const sectionTitleSx = { fontSize: '0.875rem', fontWeight: 600, color: 'text.primary', mb: 1.5, display: 'block' };
const inputFieldSx = { '& .MuiOutlinedInput-root': { borderRadius: 1.25, fontSize: '0.875rem', '& fieldset': { borderColor: 'divider' } } };

const CreateUserForm = ({ roles = [], onSuccess }) => {
  const { user } = useUser();
  const defaultRole = roles.length ? roles[0].value : 'front_office';
  const [form, setForm] = useState({ username: '', password: '', mobile: '', address: '', role: defaultRole });

  useEffect(() => {
    if (roles.length && !roles.some((r) => r.value === form.role)) {
      setForm((prev) => ({ ...prev, role: roles[0].value }));
    }
  }, [roles]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);
    try {
      await axios.post(
        '/api/auth/register',
        form
      );
      setSuccess('Employee added successfully!');
      setForm({ username: '', password: '', mobile: '', address: '', role: defaultRole });
      if (onSuccess) onSuccess();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to add employee');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit} id="create-user-form">
      <Typography sx={sectionTitleSx}>Employee Details</Typography>
      <Box sx={{ mb: 2.5 }}>
        <Typography sx={labelSx}>Username *</Typography>
        <TextField
          name="username"
          value={form.username}
          onChange={handleChange}
          fullWidth
          required
          size="small"
          placeholder="Username"
          autoFocus
          sx={inputFieldSx}
        />
      </Box>
      <Box sx={{ mb: 2.5 }}>
        <Typography sx={labelSx}>Password *</Typography>
        <TextField
          name="password"
          type="password"
          value={form.password}
          onChange={handleChange}
          fullWidth
          required
          size="small"
          placeholder="Password (min 6 characters)"
          sx={inputFieldSx}
        />
      </Box>
      <Box sx={{ mb: 2.5 }}>
        <Typography sx={labelSx}>Mobile *</Typography>
        <TextField
          name="mobile"
          value={form.mobile}
          onChange={handleChange}
          fullWidth
          required
          size="small"
          placeholder="Mobile (unique)"
          sx={inputFieldSx}
        />
      </Box>
      <Box sx={{ mb: 2.5 }}>
        <Typography sx={labelSx}>Address</Typography>
        <TextField
          name="address"
          value={form.address}
          onChange={handleChange}
          fullWidth
          size="small"
          placeholder="Address"
          multiline
          minRows={2}
          sx={inputFieldSx}
        />
      </Box>
      <Box sx={{ mb: 2 }}>
        <Typography sx={labelSx}>Role *</Typography>
        <TextField
          select
          name="role"
          value={form.role}
          onChange={handleChange}
          fullWidth
          required
          size="small"
          sx={inputFieldSx}
        >
          {(roles || []).map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>
      </Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
      <Button
        type="submit"
        form="create-user-form"
        variant="contained"
        color="primary"
        disabled={loading}
        sx={{ textTransform: 'none', fontWeight: 500 }}
      >
        {loading ? <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><CircularProgress size={18} sx={{ color: 'white' }} />Adding...</Box> : 'Add Employee'}
      </Button>
    </Box>
  );
};

export default CreateUserForm;
